import { mat4 } from 'gl-matrix'
import { GLContext } from 'nanogl/types'
import MaterialOverrideExtension from 'nanogl-gltf/lib/extensions/MaterialOverrideExtension'
import UnlitPass from 'nanogl-pbr/UnlitPass'

import { RenderContext } from '@/webgl/core/RenderContext'
import RenderPass from '@/webgl/core/RenderPass'
import DebugDraw from '@/webgl/dev/debugDraw/DebugDraw'
import gui from '@/webgl/dev/gui'
import { TextStyle } from '@/webgl/engine/FontAtlas'
import FontAtlasResource from '@/webgl/resources/FontAtlasResource'
import GltfResource from '@/webgl/resources/GltfResource'
import { CollectibleEntity } from '@/webgl/runner/entities/CollectibleEntity'
import { TimeEntity } from '@/webgl/runner/entities/Time'
import GameWorld from '@/webgl/runner/GameWorld'
import ILoadable from '@/webgl/runner/interfaces/ILoadable'
import { IRenderable } from '@/webgl/runner/interfaces/IRenderable'
import CollectibleGlowRenderer from '@/webgl/runner/rendering/CollectibleGlowRenderer'
import MeshRendererProxy from '@/webgl/runner/rendering/MeshRendererProxy'
import PriceTagRenderer from '@/webgl/runner/rendering/PriceTagRenderer'
import TextRenderer from '@/webgl/runner/rendering/TextRenderer'

export enum CollectibleResourceType {
  Pack = 0,
  Text = 1,
}

export type CollectibleResource = {
  request?: string
  id?: string
  text?: CollectibleResourceText
  font?: string
  type?: CollectibleResourceType
}

export type CollectibleResourceText = {
  font: string
  style?: TextStyle
  values: CollectibleResourceTextValues[]
}

export type CollectibleResourceTextValues = {
  id: string
  content: string
  style?: TextStyle
}

export default class CollectibleRenderer implements ILoadable {
  private renderers: Record<string, IRenderable>
  private resources: CollectibleResource[] = []

  private glowRenderer: CollectibleGlowRenderer
  private priceTagRenderer: PriceTagRenderer

  private debug: boolean = false

  private sorted: CollectibleEntity[] = []
  private highlighted: CollectibleEntity[] = []

  private cullfaceEnabled: boolean = false

  constructor(
    readonly gl: GLContext,
    readonly world: GameWorld
  ) {
    this.renderers = {}
    this.world = world

    this.glowRenderer = new CollectibleGlowRenderer(gl)
    this.priceTagRenderer = new PriceTagRenderer(gl, this.world)
    this.world.root.add(this.priceTagRenderer.node)

    const fld = gui.folder('Collectibles')
    fld.add(this, 'debug')
  }

  disableCullface() {
    this.cullfaceEnabled = false
  }

  getRenderer(id: string) {
    return this.renderers[id]
  }

  public setResources(resources: CollectibleResource[]) {
    this.resources = resources
  }

  public async load(abort: AbortSignal): Promise<void> {
    for (const resource of this.resources) {
      switch (resource.type) {
        case undefined:
        case CollectibleResourceType.Pack:
          await this.loadPack(resource, abort)
          break
        case CollectibleResourceType.Text:
          await this.loadText(resource, abort)
          break
      }
    }

    await this.priceTagRenderer.load(abort)
  }

  async loadPack(resource: CollectibleResource, abort: AbortSignal) {
    const overrides = new MaterialOverrideExtension()

    overrides.overridePass('', (ctx, m) => {
      const passInstance = m.getPass('color')
      const sourcePass = passInstance.pass as UnlitPass
      passInstance.pass.glconfig.enableCullface(this.cullfaceEnabled)

      const colorMask = new UnlitPass()
      colorMask.baseColor.attachConstant([1, 1, 1])
      colorMask.baseColorFactor.proxy(sourcePass.baseColorFactor)
      colorMask.alphaMode.proxy(sourcePass.alphaMode)
      colorMask.alpha.proxy(sourcePass.alpha)
      colorMask.alphaCutoff.proxy(sourcePass.alphaCutoff)

      m.addPass(colorMask, RenderPass.COLOR_MASK)
      return passInstance.pass
    })

    const gltf = await new GltfResource(resource.request, this.gl, { extensions: [overrides] }).load(abort)

    for (const mesh of gltf.meshes) {
      this.renderers[mesh.name] = new MeshRendererProxy(gltf, mesh)
    }
  }

  async loadText(resource: CollectibleResource, abort: AbortSignal) {
    const font = await new FontAtlasResource(resource.text.font, this.gl).load(abort)
    this.priceTagRenderer.setFont(font)

    for (const text of resource.text.values) {
      const style = {}

      if (resource.text.style) Object.assign(style, resource.text.style)

      if (text.style) Object.assign(style, text.style)

      const renderer = new TextRenderer(this.gl)
      renderer.setStyle(style)
      renderer.setFont(font)
      renderer.setText(text.content)
      this.renderers[text.id] = renderer
    }
  }

  getRendererId(collectible: CollectibleEntity) {
    let id = collectible.data.id

    if (collectible.variant !== undefined) {
      id = collectible.variant
    }

    const cswitch = collectible.switch

    if (cswitch && cswitch.apply && this.renderers[id + '_' + cswitch.suffix] != undefined) id += '_' + cswitch.suffix

    return id
  }

  public preRender(time: TimeEntity) {
    this.sorted = [...this.world.collectibles]
    this.sorted.sort((a, b) => a.node.z - b.node.z)

    this.highlighted = this.sorted.filter((c) => c.highlight)

    const priced = this.sorted.filter((c) => c.priceID !== undefined)
    const mode = this.world.shopping ? this.world.shopping.state.mode : undefined
    this.priceTagRenderer.prepare(priced, mode)
    this.glowRenderer.preRender(time.unscaledDeltaTime)
  }

  rttPass(context: RenderContext) {
    if (this.highlighted.length == 0) return

    this.glowRenderer.prepare(context)

    for (let i = 0; i < this.highlighted.length; i++) {
      const id = this.getRendererId(this.highlighted[i])
      this.renderers[id].node._wmatrix.set(this.highlighted[i].node._wmatrix)
      this.renderers[id].render(context)
    }

    this.glowRenderer.composite()
  }

  public render(context: RenderContext) {
    if (this.highlighted.length > 0) {
      this.glowRenderer.render(context)
    }

    for (const collectible of this.sorted) {
      const id = this.getRendererId(collectible)

      if (!this.renderers[id]) throw new Error(`No renderer for collectible ${id}`)

      if (this.renderers[id] instanceof TextRenderer) {
        ;(this.renderers[id] as TextRenderer).blur = collectible.blur
      }

      this.renderers[id].node._wmatrix.set(collectible.node._wmatrix)
      this.renderers[id].render(context)
    }

    this.priceTagRenderer.render(context)
    this.renderDebug()
  }

  renderDebug() {
    if (this.debug) {
      for (const collectible of this.world.collectibles) {
        const m4 = mat4.create()
        m4.set(collectible.node._wmatrix)
        m4[13] = 0
        DebugDraw.drawGuizmo(m4)
      }
    }
  }
}
